/**
 * Students Service
 * 
 * Handles all student-related API calls.
 */

import api from '../utils/api';

/**
 * Get all students
 * @param {Object} params - Query parameters (skip, limit, center, field, etc.)
 * @returns {Promise} - Get students response
 */
export const getStudents = async (params = {}) => {
  try {
    // Build query parameters
    const queryParams = {};
    if (params.skip !== undefined) queryParams.skip = params.skip;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.center) queryParams.center = params.center;
    if (params.field) queryParams.field = params.field;
    if (params.search) queryParams.search = params.search;

    const queryString = new URLSearchParams(queryParams).toString();
    const endpoint = `/v1/students/${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(endpoint);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        pagination: response.pagination,
        message: response.message || 'Students retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve students',
      data: null,
      pagination: null,
    };
  } catch (error) {
    console.error('Get students error:', error);
    
    let errorMessage = 'Failed to retrieve students. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Get a single student by ID
 * @param {string} studentId - Student ID
 * @returns {Promise} - Get student response
 */
export const getStudent = async (studentId) => {
  try {
    const response = await api.get(`/v1/students/${studentId}`);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Student retrieved successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to retrieve student',
      data: null,
    };
  } catch (error) {
    console.error('Get student error:', error);
    
    let errorMessage = 'Failed to retrieve student. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Update a student application (full update using PUT)
 * @param {string} studentId - Student ID
 * @param {Object} studentData - Student data to update
 * @returns {Promise} - Update student response
 */
export const updateStudent = async (studentId, studentData) => {
  try {
    const response = await api.put(`/v1/students/${studentId}`, studentData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Student updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update student',
      data: null,
    };
  } catch (error) {
    console.error('Update student error:', error);
    
    let errorMessage = 'Failed to update student. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Partially update a student application (PATCH)
 * @param {string} studentId - Student ID
 * @param {Object} studentData - Fields to update (only include fields to update)
 * @returns {Promise} - Update student response
 */
export const updateStudentPartial = async (studentId, studentData) => {
  try {
    const response = await api.patch(`/v1/students/${studentId}`, studentData);

    if (response.status === 'success' && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Student updated successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to update student',
      data: null,
    };
  } catch (error) {
    console.error('Update student partial error:', error);
    
    let errorMessage = 'Failed to update student. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.details && Array.isArray(errorData.details)) {
        const detailMessages = errorData.details.map(d => d.message).join(', ');
        if (detailMessages) {
          errorMessage = detailMessages;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

/**
 * Delete a student application
 * @param {string} studentId - Student ID
 * @returns {Promise} - Delete student response
 */
export const deleteStudent = async (studentId) => {
  try {
    const response = await api.delete(`/v1/students/${studentId}`);

    if (response.status === 'success') {
      return {
        success: true,
        message: response.message || 'Student deleted successfully',
      };
    }

    return {
      success: false,
      error: response.message || 'Failed to delete student',
    };
  } catch (error) {
    console.error('Delete student error:', error);
    
    let errorMessage = 'Failed to delete student. Please try again.';
    
    if (error.response) {
      const errorData = error.response;
      if (errorData.error) {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

const studentsService = {
  getStudents,
  getStudent,
  updateStudent,
  updateStudentPartial,
  deleteStudent,
};
export default studentsService;

